import React, { useCallback, useMemo, useRef, useState } from "react";
import { Scrollbars } from "react-custom-scrollbars-2";
import { useReconcileGustoTransfersMutation } from "@api/gusto.api";
import { usePayrollFinancialAccounts } from "@hooks/useFinancialAccounts";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { Button } from "@design-system/Button/Button";
import { useEntities } from "@hooks/useEntities";
import { pluralize } from "@common/helpers/string";
import { Drawer } from "@design-system/Drawer/Drawer";
import { OverlayContainerProvider } from "@design-system/overlay/OverlayContainerProvider";
import { HStack } from "@design-system/HStack/HStack";
import { useTransactionsQuery } from "../../useTransactionsQuery";
import { SelectedTransaction } from "../transactionsBulkActionsContext";
import { RulesDrawerHeader } from "../../../settings/Rules/RulesDrawer/RulesDrawerHeader";
import { VirtualTransactionMatch } from "./GustoVirtualMatch";
import styles from "./ReconcileGustoTransfersModal.module.scss";

interface Props {
    show: boolean;
    onClose: () => void;
    selectedTransactions: SelectedTransaction[];
}

export const ReconcileGustoTransfersModal: React.FC<Props> = ({
    show,
    onClose,
    selectedTransactions,
}) => {
    const entities = useEntities();
    const transactionIds = useMemo(
        () => selectedTransactions.map((t) => t.id),
        [selectedTransactions],
    );
    const {
        query: { data: transactionsResponse },
    } = useTransactionsQuery({
        filters: {
            transactionIds,
            entityIds: entities.map((entity) => entity.id),
        },
        page: 1,
    });
    const [isLoading, setIsLoading] = useState(false);
    const gustoAccounts = usePayrollFinancialAccounts();
    const reconcileGustoTransfersMutation =
        useReconcileGustoTransfersMutation();

    const selectedGustoAccount = useMemo(
        () =>
            gustoAccounts.find(
                (account) =>
                    account.entity?.id === selectedTransactions[0]?.entity.id,
            ),
        [gustoAccounts, selectedTransactions],
    );
    const handleCreateAndReconcile = useCallback(async () => {
        if (!selectedGustoAccount) {
            return;
        }

        const reconcileTransfers = async () => {
            setIsLoading(true);
            await reconcileGustoTransfersMutation.mutateAsync({
                gustoAccountId: selectedGustoAccount.id,
                transactionIds,
            });
            onClose();
        };
        await reconcileTransfers().finally(() => {
            setIsLoading(false);
        });
    }, [
        selectedGustoAccount,
        reconcileGustoTransfersMutation,
        transactionIds,
        onClose,
    ]);
    const containerRef = useRef<HTMLDivElement>(null);

    return (
        <Drawer
            open={show}
            onClose={onClose}
            direction="right"
            modal={false}
            contentClassName={styles.drawer}
            headerAddonLeft={<RulesDrawerHeader text="Reconcile Gusto" />}
            size="fullScreen"
        >
            <OverlayContainerProvider containerRef={containerRef}>
                <div ref={containerRef} className={styles.drawerContent}>
                    {selectedGustoAccount ? (
                        <Scrollbars>
                            <HStack align="center">
                                <div>
                                    Create {transactionIds.length}{" "}
                                    {pluralize(
                                        "transaction",
                                        transactionIds.length,
                                    )}{" "}
                                    to reconcile your Gusto account.
                                </div>
                            </HStack>
                            {(transactionsResponse?.data ?? []).map(
                                (transaction) => (
                                    <VirtualTransactionMatch
                                        key={transaction.id}
                                        gustoAccount={selectedGustoAccount}
                                        transaction={transaction}
                                    />
                                ),
                            )}
                        </Scrollbars>
                    ) : null}

                    <HStack justify="end" gap={12} className={styles.footer}>
                        <Button
                            variant="secondary"
                            size="md"
                            onClick={onClose}
                            disabled={isLoading}
                        >
                            Cancel
                        </Button>
                        <ButtonWithLoader
                            variant="primary"
                            size="md"
                            loading={isLoading}
                            disabled={!selectedGustoAccount}
                            onClick={handleCreateAndReconcile}
                        >
                            Create and reconcile
                        </ButtonWithLoader>
                    </HStack>
                </div>
            </OverlayContainerProvider>
        </Drawer>
    );
};
